import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Profile API service
export const profileApi = {
  // Get user profile
  getProfile: async () => {
    const token = localStorage.getItem('auth_token');
    const response = await axios.get(`${API_BASE_URL}/user/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Update user profile
  updateProfile: async (profileData: any) => {
    const token = localStorage.getItem('auth_token');
    const response = await axios.put(`${API_BASE_URL}/user/me`, profileData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Upload avatar
  uploadAvatar: async (file: File) => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_BASE_URL}/user/avatar`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Upload banner
  uploadBanner: async (file: File) => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_BASE_URL}/user/banner`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get user by ID (public profile)
  getUserById: async (userId: number) => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await axios.get(`${API_BASE_URL}/user/${userId}`, { headers });
    return response.data;
  },



  // Get dashboard statistics
  getDashboardStats: async (period: string = 'month', accountType?: string) => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams();
    params.append('period', period);
    if (accountType) {
      params.append('account_type', accountType);
    }
    
    const response = await axios.get(`${API_BASE_URL}/user/dashboard/stats?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Get notifications
  getNotifications: async (limit = 20, offset = 0) => {
    const token = localStorage.getItem('auth_token');
    const response = await axios.get(`${API_BASE_URL}/user/notifications`, {
      params: { limit, offset },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    // Return the notifications array directly (not the entire response object)
    return (response.data as any)?.notifications || [];
  },

  // Mark notification as read
  markNotificationRead: async (notificationId: number) => {
    const token = localStorage.getItem('auth_token');
    const response = await axios.put(`${API_BASE_URL}/user/notifications/${notificationId}/read`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  getUnpaidRentRooms: async () => {
    const token = localStorage.getItem('auth_token');
    const response = await axios.get(`${API_BASE_URL}/user/dashboard/unpaid-rent-rooms`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data as UnpaidRentReport;
  },

};



// Dashboard statistics interface
export interface UnpaidRentRoomItem {
  property_id: number;
  contract_id: number;
  pay_room_code?: string | null;
  room_label: string;
  room_number?: string | null;
  address?: string | null;
  property_title: string;
  tenant_name?: string | null;
  period_start: string;
  period_end?: string | null;
  period_label: string;
  payment_term?: string | null;
  due_date: string;
  amount: number;
  rent_payment_day?: number;
}

export interface UnpaidRentReport {
  count: number;
  total_overdue_periods: number;
  items: UnpaidRentRoomItem[];
  as_of_date: string;
}

export interface DashboardStats {
  total_properties: number;
  active_properties: number;
  total_revenue: number;
  total_spent: number;
  total_commission: number;
  total_views: number;
  total_favorites: number;
  response_rate: number;
  customer_rating: number;
  total_reviews: number;
  // Contract Statistics
  total_contracts?: number;
  active_contracts?: number;
  pending_contracts?: number;
  expiring_contracts?: number;
  contracts_change_percent?: number;
  // Payment Statistics
  total_payments?: number;
  pending_payments?: number;
  failed_payments?: number;
  average_payment_amount?: number;
  // Comparison data
  properties_change_percent?: number;
  active_properties_change_percent?: number;
  revenue_change_percent?: number;
  pending_actions?: number;
  pending_actions_change?: number;
  // Monthly revenue data for chart (12 months)
  monthly_revenue?: number[];
  // Weekly active rentals with dates
  weekly_active_rentals?: Array<{
    contract_id: number;
    property_id: number;
    property_name: string;
    start_date?: string;
    end_date?: string;
    amount: number;
    rental_date: string;
  }>;
  // Profile-specific statistics
  profile_specific_stats?: {
    // Tenant
    rental_contracts?: number;
    active_rental_contracts?: number;
    active_contracts?: number;
    total_rental_cost?: number;
    payment_count?: number;
    pending_payments?: number;
    pending_bills?: Array<{
      id: number;
      property_id: number;
      bill_type: string;
      amount: number;
      billing_period_start?: string;
      billing_period_end?: string;
      due_date?: string;
      status?: string;
      description?: string;
    }>;
    total_paid?: number;
    total_deposit?: number;
    renting_properties?: Array<{
      property_name: string;
      start_date: string;
      end_date: string;
      monthly_payment: number;
      pending_payment: number;
      deposit: number;
    }>;
    // Landlord
    total_tenants?: number;
    occupancy_rate?: number;
    average_rental_duration_days?: number;
    maintenance_requests_total?: number;
    maintenance_requests_pending?: number;
    total_deposit?: number;
    owned_properties?: number;
    shared_properties?: number;
    pet_friendly_count?: number;
  };

  // Monthly payments data for Tenant (12 months)
  monthly_payments?: number[];

  // ===== FinTech METRICS =====
  // Contract Analytics
  contract_analytics?: {
    total_value: number;
    avg_contract_value: number;
    min_contract_value: number;
    max_contract_value: number;
    total_contracts_count: number;
    total_received: number;
    total_received_current_year?: number;
    total_received_current_month?: number;
    pending_amount: number;
    monthly_receivable?: number;
    outstanding_amount?: number;
    completion_rate: number;
    avg_contract_duration_months: number;
    contracts_expiring_30days: number;
  };

  // Property-level analytics
  top_properties?: Array<{
    property_id: number;
    property_title: string;
    property_price: number;
    total_value: number;
    active_contracts: number;
    avg_contract_value: number;
    occupancy_duration: number;
    roi_percent: number;
    profit: number;
    trend_3m: number;
    status: string;
  }>;

  // Financial Metrics
  financial_metrics?: {
    total_revenue: number;
    recurring_revenue: number;
    one_time_revenue: number;
    total_costs: number;
    net_profit: number;
    profit_margin: number;
    financial_health_score: number;
  };

  // Alerts
  alerts?: Array<{
    alert_id: string;
    type: string;
    severity: 'high' | 'medium' | 'low';
    title: string;
    message: string;
    action_url?: string;
  }>;

  // Forecast
  forecast?: {
    forecast_next_month: number;
    forecast_confidence: number;
  };

  // Trends
  trends?: {
    revenue_trend_percent: number;
    contracts_trend_percent: number;
    direction: 'up' | 'down' | 'stable';
  };
}

// User notification interface
export interface UserNotification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  is_important: boolean;
  action_url?: string;
  created_at: string;
  read_at?: string;
}

// Profile update interface
export interface ProfileUpdateData {
  full_name?: string;
  phone?: string;
  address?: string;
  bio?: string;
  company?: string;
  website?: string;
  license?: string;
  tax_id?: string;
  license_number?: string;
  specialization?: string;
  experience?: string;
  business_type?: string;
  industry?: string;
  employee_count?: string;
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  twitter?: string;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  push_notifications?: boolean;
  profile_visibility?: string;
  show_contact_info?: boolean;
  show_properties?: boolean;
}


